/* ==========================================================================
   Focus choreography — autofocus the right field on every path into view
   Avanti Design Rules / ux/focus-choreography

   Dependency-free, no build step.

     const fc = new FocusChoreography({
       section: '[data-focus-section]',   // each section owns one field
       onDecision: (info) => console.log(info),
     });
     fc.setMode('choreographed');   // 'off' | 'naive' | 'choreographed'

   The scroll trigger is the dangerous one. Focusing a text field takes Space,
   PageDown and the arrows away from the scroller, so a naive "focus on
   intersection" implementation traps the user's scroll. Every guard below
   exists to make sure focus only ever lands when the user has *arrived*, not
   while they are still travelling.
   ========================================================================== */

const EDITABLE = 'input, textarea, select, [contenteditable=""], [contenteditable="true"]';
const TEXTISH = [
  'input:not([type])', 'input[type="text"]', 'input[type="search"]',
  'input[type="email"]', 'input[type="url"]', 'input[type="tel"]',
  'input[type="number"]', 'input[type="password"]', 'textarea',
  '[contenteditable=""]', '[contenteditable="true"]',
].join(',');

const isEditable = (el) => !!el && el.matches && el.matches(EDITABLE);
const coarsePointer = () => window.matchMedia('(pointer: coarse)').matches;

function isVisible(el) {
  if (!el || el.disabled || el.readOnly) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

class FocusChoreography {
  constructor(opts = {}) {
    this.sectionSel = opts.section || '[data-focus-section]';
    this.fieldSel = opts.field || null;
    this.idleMs = opts.idleMs ?? 140;
    // A section qualifies on its own merit (minScore) and must also be the
    // unambiguous winner (clarity). Two separate questions, two separate knobs.
    this.minScore = opts.minScore ?? 0.5;
    this.clarity = opts.clarity ?? 1.3;
    this.typeAhead = opts.typeAhead !== false;
    this.escapeBlurs = opts.escapeBlurs !== false;
    this.respectCoarsePointer = opts.respectCoarsePointer !== false;
    this.onDecision = opts.onDecision || (() => {});
    this.mode = opts.mode || 'choreographed';
    this.scroller = opts.scroller || null;   // null = the viewport

    this.sections = Array.from(document.querySelectorAll(this.sectionSel));
    this.visible = new Set();    // sections currently intersecting at all
    this.served = new Set();     // sections already focused since they entered
    this.buffer = '';            // type-ahead
    this.bufferAt = 0;
    this.scrolling = false;
    this._idleTimer = null;

    this._onScroll = this._onScroll.bind(this);
    this._onKeydown = this._onKeydown.bind(this);

    const root = this.scroller || null;
    this.io = new IntersectionObserver((entries) => this._onIntersect(entries), {
      root,
      threshold: [0, 0.02, 0.1, 0.25, 0.4, 0.55, 0.7, 0.85, 1],
    });
    this.sections.forEach((s) => this.io.observe(s));

    (this.scroller || window).addEventListener('scroll', this._onScroll, { passive: true });
    document.addEventListener('keydown', this._onKeydown, true);
  }

  setMode(mode) {
    this.mode = mode;
    this.served.clear();
    this.buffer = '';
    this._report('mode', null, `Modus: ${mode}`);
  }

  destroy() {
    this.io.disconnect();
    (this.scroller || window).removeEventListener('scroll', this._onScroll);
    document.removeEventListener('keydown', this._onKeydown, true);
    clearTimeout(this._idleTimer);
  }

  /* ------------------------------------------------------------ observers */

  _onIntersect(entries) {
    // The observer answers one question only: which sections are on screen at
    // all? Scores are measured fresh in _decide(), because the geometry keeps
    // changing between threshold crossings and cached ratios go stale.
    entries.forEach((e) => {
      if (e.isIntersecting) {
        this.visible.add(e.target);
      } else {
        this.visible.delete(e.target);
        this.served.delete(e.target);   // re-arm, so returning focuses again
      }
    });

    if (this.mode === 'naive') {
      // Deliberately wrong: fires mid-scroll, every tick, with no dominance
      // test and no once-per-entry guard. This is the version users hate.
      const hit = entries.find((e) => e.isIntersecting && e.intersectionRatio > 0.2);
      if (hit) {
        const field = this._field(hit.target);
        if (field && document.activeElement !== field) {
          field.focus({ preventScroll: true });
          this._report('focus', hit.target, 'naiv — mitten im Scrollen fokussiert');
        }
      }
    }
  }

  _onScroll() {
    if (this.mode !== 'choreographed') return;
    this.scrolling = true;
    clearTimeout(this._idleTimer);
    this._idleTimer = setTimeout(() => {
      this.scrolling = false;
      this._decide();
    }, this.idleMs);
  }

  /* -------------------------------------------------------------- scoring */

  _rootRect() {
    if (this.scroller) {
      const r = this.scroller.getBoundingClientRect();
      return { top: r.top, height: r.height };
    }
    return { top: 0, height: window.innerHeight };
  }

  /**
   * How strongly does this section claim the screen right now?
   *
   *   presence  — max(share of the viewport it covers,
   *                   share of itself that is visible)
   *               Two measures because either one alone is wrong: a section
   *               taller than the screen never reaches high self-visibility,
   *               and a section shorter than the screen never reaches high
   *               viewport coverage. A short form fully in view scores 1.
   *   centre    — 1 when the section's middle sits on the viewport's middle,
   *               falling off with distance. This is the "what am I looking
   *               at" signal, and it is what separates two forms that are
   *               both on screen.
   */
  _score(section) {
    const root = this._rootRect();
    const r = section.getBoundingClientRect();
    const secH = r.height || 1;
    const visible = Math.max(0, Math.min(r.bottom, root.top + root.height) - Math.max(r.top, root.top));
    if (visible <= 0) return { presence: 0, centre: 0, score: 0 };

    const presence = Math.max(visible / root.height, visible / secH);

    // Distance is measured against *half* the viewport: a section whose middle
    // sits half a screen from the middle of the screen is at the edge, and
    // should score 0 for centredness. Measuring against the full viewport made
    // the falloff so gentle that two stacked forms were never distinguishable.
    const secCentre = r.top + secH / 2;
    const rootCentre = root.top + root.height / 2;
    const centre = 1 - Math.min(1, Math.abs(secCentre - rootCentre) / (root.height / 2));

    // Presence saturates at 1 for anything fully on screen, so it stops
    // discriminating exactly when several forms are visible at once. Centre is
    // therefore weighted equally — it is the only signal left in that case.
    return { presence, centre, score: presence * (0.5 + 0.5 * centre) };
  }

  /**
   * Public: what the choreographer currently thinks of every section, best
   * first. Scored from live geometry rather than from observer bookkeeping, so
   * a debug overlay and the real decision can never disagree — and so the
   * ranking still works anywhere IntersectionObserver does not.
   */
  ranking() {
    return this.sections
      .map((section) => Object.assign({ section }, this._score(section)))
      .sort((a, b) => b.score - a.score);
  }

  /** Public: the verdict the next decision would reach, and why. */
  explain() {
    const ranked = this.ranking();
    const best = ranked[0];
    const runnerUp = ranked[1] ? ranked[1].score : 0;
    if (!best || best.score < this.minScore) {
      return { verdict: 'none', reason: 'schwach', ranked };
    }
    if (runnerUp > 0 && best.score < runnerUp * this.clarity) {
      return { verdict: 'none', reason: 'mehrdeutig', ranked };
    }
    if (this.served.has(best.section)) {
      return { verdict: 'served', reason: 'schon bedient', winner: best.section, ranked };
    }
    return { verdict: 'focus', reason: 'eindeutig', winner: best.section, ranked };
  }

  /* ------------------------------------------------------------- decision */

  _decide() {
    if (this.mode !== 'choreographed') return;

    // Guard: a modal above owns focus. Never reach past it.
    // Kontor patch: a dialog inside a hidden ancestor (a closed tour card) is not open.
    if ([...document.querySelectorAll('[aria-modal="true"]')].some((m) => m.getClientRects().length > 0)) {
      return this._report('skip', null, 'übersprungen — ein Dialog besitzt den Fokus');
    }

    // Guard: the user is already typing somewhere. Never interrupt that.
    const active = document.activeElement;
    if (isEditable(active)) {
      return this._report('skip', null, 'übersprungen — der Nutzer tippt bereits');
    }

    // Guard: on a touch device this would throw the keyboard over the content
    // the user came to read. Deliberate gestures still focus; scrolling does not.
    if (this.respectCoarsePointer && coarsePointer()) {
      return this._report('skip', null, 'übersprungen — Touch-Gerät, Scrollen fokussiert nicht');
    }

    const ranked = this.ranking();
    const best = ranked[0] ? ranked[0].section : null;
    const bestScore = ranked[0] ? ranked[0].score : 0;
    const runnerUp = ranked[1] ? ranked[1].score : 0;

    // Question one: is anything substantially on screen?
    if (!best || bestScore < this.minScore) {
      return this._report('skip', best,
        `übersprungen — nichts beherrscht den Schirm (Score ${bestScore.toFixed(2)} < ${this.minScore})`);
    }

    // Question two: is it the *obvious* one? Two forms side by side is a
    // genuinely ambiguous situation, and the right answer to an ambiguous
    // situation is to do nothing. Focusing the wrong field is worse than
    // focusing none: it puts the user's typing somewhere they did not look.
    if (runnerUp > 0 && bestScore < runnerUp * this.clarity) {
      return this._report('skip', best,
        `übersprungen — mehrdeutig (${bestScore.toFixed(2)} vs. ${runnerUp.toFixed(2)}, `
        + `braucht ${this.clarity}×)`);
    }
    if (best.hasAttribute('data-focus-skip')) {
      return this._report('skip', best, 'übersprungen — Sektion opt-out');
    }
    if (this.served.has(best)) {
      return this._report('skip', best, 'übersprungen — in dieser Sektion schon bedient');
    }

    const field = this._field(best);
    if (!field) return this._report('skip', best, 'übersprungen — kein Feld in der Sektion');
    if (document.activeElement === field) {
      this.served.add(best);
      return;
    }

    this._focus(field, best, bestScore);
  }

  _focus(field, section, score) {
    field.focus({ preventScroll: true });
    // Caret to the end of any existing draft, never over the top of it.
    if (field.setSelectionRange && typeof field.value === 'string') {
      const n = field.value.length;
      try { field.setSelectionRange(n, n); } catch { /* number inputs throw */ }
    }
    this.served.add(section);
    this._flushBuffer(field);
    this._report('focus', section,
      `fokussiert — Score ${score.toFixed(2)}, eindeutig, Scrollen steht still`);
  }

  _field(section) {
    // Explicit beats inferred, always. If the author said which field matters,
    // there is nothing to work out.
    const marked = section.querySelector('[data-focus-primary], [autofocus]');
    if (marked && isVisible(marked)) return marked;

    const el = this.fieldSel
      ? section.querySelector(this.fieldSel)
      : section.querySelector(TEXTISH);
    return isVisible(el) ? el : null;
  }

  /* ------------------------------------------------------------ keyboard */

  _onKeydown(e) {
    // Esc gives the page back: blurring returns Space and PageDown to the
    // scroller. Without this, autofocus on scroll is a trap.
    if (this.escapeBlurs && e.key === 'Escape' && isEditable(document.activeElement)) {
      const section = this.sections.find((s) => s.contains(document.activeElement));
      document.activeElement.blur();
      if (section) this.served.add(section);   // do not immediately re-focus
      this._report('blur', section, 'Esc — Fokus freigegeben, Scrolltasten gehören wieder der Seite');
      return;
    }

    if (!this.typeAhead || this.mode !== 'choreographed') return;
    if (isEditable(document.activeElement)) return;
    if (!this.scrolling) return;
    if (e.metaKey || e.ctrlKey || e.altKey) return;
    // Space is a scroll key before it is a character. Never swallow it.
    if (e.key === ' ' || e.key.length !== 1) return;

    if (Date.now() - this.bufferAt > 1200) this.buffer = '';
    if (this.buffer.length < 24) this.buffer += e.key;
    this.bufferAt = Date.now();
    e.preventDefault();
    this._report('buffer', null, `getippt, bevor der Fokus da war: „${this.buffer}"`);
  }

  _flushBuffer(field) {
    if (!this.buffer) return;
    if (Date.now() - this.bufferAt > 1200) { this.buffer = ''; return; }
    if (typeof field.value === 'string') {
      field.value += this.buffer;
      field.dispatchEvent(new Event('input', { bubbles: true }));
    }
    this._report('flush', null, `Puffer eingesetzt: „${this.buffer}"`);
    this.buffer = '';
  }

  _report(kind, section, message) {
    this.onDecision({ kind, section, message, mode: this.mode });
  }
}

/* ==========================================================================
   FormChain — the second scale of the same method.

   FocusChoreography gets the caret *to* a form. FormChain moves it *through*
   one, so a whole form is: scroll, type, Enter, type, Enter, done. The mouse
   is never involved and neither is Tab.

     new FormChain('#signup', { onSubmit: (form) => save(form) });

   Enter advances to the next field and submits on the last one. In a textarea
   Enter still inserts a newline — Cmd/Ctrl+Enter advances there instead, which
   is the convention people already have in their fingers.
   ========================================================================== */

class FormChain {
  constructor(root, opts = {}) {
    this.root = typeof root === 'string' ? document.querySelector(root) : root;
    if (!this.root) throw new Error('[FormChain] root not found');
    this.fieldSel = opts.field || `${TEXTISH}, select`;
    this.onSubmit = opts.onSubmit || null;
    this.submitOnLast = opts.submitOnLast !== false;
    this.onAdvance = opts.onAdvance || (() => {});
    this.onInvalid = opts.onInvalid || (() => {});

    this._onKeydown = this._onKeydown.bind(this);
    this.root.addEventListener('keydown', this._onKeydown);
  }

  fields() {
    return Array.from(this.root.querySelectorAll(this.fieldSel)).filter(isVisible);
  }

  destroy() {
    this.root.removeEventListener('keydown', this._onKeydown);
  }

  _onKeydown(e) {
    if (e.key !== 'Enter') return;
    const el = e.target;
    if (!el.matches || !el.matches(this.fieldSel)) return;

    // A textarea owes the user a newline on Enter. Advancing there needs the
    // explicit Cmd/Ctrl+Enter, or people lose paragraphs mid-sentence.
    const multiline = el.tagName === 'TEXTAREA' || el.isContentEditable;
    const advance = multiline ? (e.metaKey || e.ctrlKey) : !e.shiftKey;
    if (!advance) return;

    e.preventDefault();
    const list = this.fields();
    const next = list[list.indexOf(el) + 1];

    if (next) {
      // No preventScroll here: if the next field is below the fold, scrolling
      // to it is exactly what the user asked for.
      next.focus();
      if (next.setSelectionRange && typeof next.value === 'string') {
        const n = next.value.length;
        try { next.setSelectionRange(n, n); } catch { /* number inputs throw */ }
      }
      this.onAdvance(next, list.indexOf(next) + 1, list.length);
      return;
    }

    if (!this.submitOnLast) return;

    // "I pressed Enter and nothing happened" is the state that sends a hand
    // back to the mouse for good. If the form cannot be submitted, say where.
    const invalid = list.find((f) => f.checkValidity && !f.checkValidity());
    if (invalid) {
      invalid.focus();
      if (invalid.setSelectionRange && typeof invalid.value === 'string') {
        const n = invalid.value.length;
        try { invalid.setSelectionRange(n, n); } catch { /* number inputs throw */ }
      }
      if (invalid.reportValidity) invalid.reportValidity();
      this.onInvalid(invalid);
      return;
    }

    if (this.onSubmit) this.onSubmit(this.root);
    else if (this.root.requestSubmit) this.root.requestSubmit();
  }
}

/* Classic scripts by default (`<script src="focus.js">`), so a single-file page
   works from file://. For modules, append
   `export { FocusChoreography, FormChain };` and load with
   `<script type="module">`. */
if (typeof window !== 'undefined') {
  window.FocusChoreography = FocusChoreography;
  window.FormChain = FormChain;
}

export { FocusChoreography, FormChain };
